import { Check, CheckCircleRounded, DeleteRounded, DoNotDisturbRounded, EditRounded, InfoRounded, OpenInNewRounded, VillaRounded } from "@mui/icons-material";
import { Alert, AlertTitle, alpha, Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography } from "@mui/material";
import { getFirestore, setDoc, doc } from "firebase/firestore";
import { getStorage, ref, uploadString } from "firebase/storage";
import { DateTime } from "luxon";
import { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Vacation } from "../../types/Vacation";
import { VacationRequestInfoBox } from "./VacationRequestInfoBox";
import { useSnackbar } from 'notistack';
import { RequestStatus } from "../../types/Type";
import { updateVacation, updateVacationRequestStatus } from "../../utils/updateVacation";
import { deleteVacation } from "../../utils/deleteVacation";
import { VacationRequestForm } from "./VacationRequestForm";
import { Employee } from "../../types/Employee";
import { hasPermission } from "../../security/Permission";
import { useAppSelector } from "../../store";
import { SecureComponent } from "../../security/SecureComponent";

interface FileBoxProps { 
  label: string, 
  type: string, 
  file?: string, 
  onUpload?: (type: string, fileName: string, data: string) => Promise<boolean>
}
const FileBox = (props: FileBoxProps) => { 
  const { label, type, file, onUpload } = props;
  const [data, setData] = useState('');
  const [fileName, setFileName] = useState('No file uploaded...');
  const [enable, setEnable] = useState(false);

  const handleInputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFileName(event.target.files[0].name);
      setEnable(true);

      const fileReader = new FileReader();
      fileReader.readAsDataURL(event.target.files[0]);
      fileReader.onload = (e) => {
        if (e.target && e.target.result && typeof e.target.result === 'string') {
          setData(e.target.result);
        }
      };
    }
  };

  const handleUploadOnClick = () => {
    onUpload && onUpload(type, fileName, data).then((s) => {
      if (s) {
        setData('');
        setFileName('No file uploaded...');
        setEnable(false);
      }
    });
  };

  return (
    <Stack 
      direction="row"
      spacing={1}
      sx={{ 
        p: 2,
        py: 0.5,
        pr: 0.5,
        borderRadius: 1,
        alignItems: 'center',
        backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
        "&:hover": {
          backgroundColor: theme => alpha(theme.palette.primary.main, 0.15)
        },
      }}
    >
      <Typography variant="button" whiteSpace="nowrap">{label}</Typography>
      <Box component="div" sx={{ display: 'flex', flexGrow: 1, mx: 2, alignItems: 'center', overflow: 'hidden' }}>
        <Typography variant="caption" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
          {file ? enable ? fileName : file : fileName}
        </Typography>
      </Box>
      <IconButton LinkComponent="a" size="small" href={file ?? ''} target="_blank" disabled={file === undefined}>
        <OpenInNewRounded />
      </IconButton>
      <SecureComponent permissions={['update.vacations']}>
        <Button component="label">
          Browse
          <input hidden type="file" onChange={handleInputOnChange} />
        </Button>
      </SecureComponent>
      <SecureComponent permissions={['update.vacations']}>
        <Button color="secondary" disabled={!enable} onClick={handleUploadOnClick}>
          Upload
        </Button>
      </SecureComponent>
    </Stack>
  )
};

export const VacationRequestPage = () => {
  const db = getFirestore();
  const storage = getStorage();
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);

  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { state } = useLocation();

  const [vacation, setVacation] = useState<Vacation>((state as any)?.vacation ?? undefined);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [showVacationRequestForm, setShowVacationRequestForm] = useState(false);
  const [updatedStartDate, setUpdatedStartDate] = useState('');
  const [updatedEndDate, setUpdatedEndDate] = useState('');
  const [disableSaveButton, setDisableSaveButton] = useState(true);

  const hideVacartionRequestForm = () => {
    setShowVacationRequestForm(false);
  };

  const handleUpdateVacation = (r: RequestStatus) => {
    if (vacation) {
      updateVacationRequestStatus(vacation, r).then(() => {
        const updatedValue: Vacation = { ...vacation, requestStatus: r };
        setVacation(updatedValue);
        enqueueSnackbar(
          r === 'Approved' 
            ? 'Vacation request has been approved.'
          : r === 'Denied'
            ? 'Vacation request has been denied.'
            : 'Vacation request status has been updated.', 
          { variant: 'success' });
      });
    }
  };

  const upload = async (type: string, fileName: string, data: string) => {
    const idNumber = vacation.idNumber;
    const id = vacation.id;
    const dateString = DateTime.utc().toFormat("yyyyMMdd_hhmmss");
    const file = type + '_' + dateString + '_' + fileName;

    const dest = `${idNumber}/Vacations/${id}/${file}`;
    return await uploadString(ref(storage, dest),  data, 'data_url')
      .then(async () => {
        const url = "https://firebasestorage.googleapis.com/v0/b/nadafa-ems.appspot.com/o/" + encodeURIComponent(dest) + "?alt=media";
        await setDoc(
          doc(db, `Employees/${idNumber}/Vacations/${id}`), 
          {
            files: {
              [type]: url,
            }
          },
          { merge: true }
        );
        return url;
      })
      .then((url) => {
        // Update selected vacation
        vacation.files = {
          ...vacation.files,
          [type]: url,
        };
        setVacation({...vacation});
        enqueueSnackbar('File has been uploaded!', { variant: 'success' });
        return true;
      })
      .catch(r => {
        enqueueSnackbar('An error has occured. ' + r, { variant: 'error' });
        return false;
      });
  };

  const handleOnDeleteClick = () => {
    if (vacation) {
      deleteVacation(vacation).then(() => {
        setOpenConfirmDelete(false);
        enqueueSnackbar('Vacation request has been deleted.', { variant: 'success' });
        setTimeout(() => navigate(-1), 250);
      });
    }
  };

  const onEditVacationFormChanged = (_e: Employee, startDate: string, endDate: string) => {
    setUpdatedStartDate(startDate);
    setUpdatedEndDate(endDate);
    setDisableSaveButton(!(vacation.dateStart !== startDate || vacation.dateEnd !== endDate));
  };

  const handleSaveVacationOnClick = () => {
    if (vacation.dateStart !== updatedStartDate || vacation.dateEnd !== updatedEndDate) {
      if (window.confirm('Are you sure you want to save the changes?')) {
        setDisableSaveButton(true);
        const updatedVacation: Vacation = {
          ...vacation,
          dateStart: updatedStartDate,
          dateEnd: updatedEndDate
        };
        updateVacation(updatedVacation).then(status => {
          setVacation({ ...updatedVacation, status });
          setShowVacationRequestForm(false);

          enqueueSnackbar('Vacation has been updated!', { variant: 'success' });
        });
      }
    }
  };

  useEffect(() => {
    setUpdatedStartDate(vacation.dateStart);
    setUpdatedEndDate(vacation.dateEnd);
  }, [vacation]);

  if (!vacation) {
    return (
      <Typography variant="caption">Please wait...</Typography>
    )
  }

  return (
    <Container disableGutters>
      <Typography variant="h5" mt={2} mb={5}>Vacation Requests</Typography>
      <Stack spacing={3} direction="row" alignItems="center" mb={2}>
        <Chip label={vacation.idNumber} />
        <Typography variant="h6">{vacation.name}</Typography>
        <SecureComponent permissions={['update.vacations', 'delete.vacations']}>
          <Stack direction='row' spacing={1} sx={{ display: 'flex', flexGrow: 1, justifyContent: 'right' }}>
            <SecureComponent permissions={['update.vacations']}>
              <Button startIcon={<EditRounded />} color="warning" onClick={() => setShowVacationRequestForm(true)}>EDIT</Button>
            </SecureComponent>
            <SecureComponent permissions={['delete.vacations']}>
              <Button startIcon={<DeleteRounded />} color="error" onClick={() => setOpenConfirmDelete(true)}>DELETE</Button>
            </SecureComponent>
          </Stack>
        </SecureComponent>
      </Stack>
      <Stack mb={2}>
        <Alert
          icon={
            vacation.status === 'Requested' 
              ? <InfoRounded /> : vacation.status === 'In Progress' 
              ? <VillaRounded /> : <CheckCircleRounded />}
          severity={vacation.status === 'Requested' ? 'info' : vacation.status === 'In Progress' ? 'warning' : 'success'}
        >
          <AlertTitle>Vacation Status</AlertTitle>
          {vacation.status === 'Requested' ? 'New vacation request. Please process.' : vacation.status === 'In Progress' ? 'Employee is currently on vacation.' : 'Vacation is now complete. Employee returned from vacation.'}
        </Alert>
      </Stack>
      <Paper>
        <VacationRequestInfoBox vacation={vacation} />
      </Paper>
      <SecureComponent permissions={['update.vacations']}>
        <Stack mt={1} spacing={1} direction="row" justifyContent="right">
          <Button 
            color="error" 
            startIcon={<DoNotDisturbRounded />} 
            disabled={vacation.requestStatus !== 'New'} 
            onClick={() => handleUpdateVacation('Denied')}
          >
            Deny
          </Button>
          <Button 
            color="success" 
            startIcon={<CheckCircleRounded />} 
            disabled={vacation.requestStatus !== 'New'} 
            onClick={() => handleUpdateVacation('Approved')}
          >
            Approve
          </Button>
        </Stack>
      </SecureComponent>
      {vacation.requestStatus === 'Approved' && (
        <Stack mt={3} spacing={1}>
          <FileBox label="Booking Date" type="booking" file={vacation.files?.booking} onUpload={upload} />
          <FileBox label="Paper Process" type="paper" file={vacation.files?.paper} onUpload={upload} />
          <FileBox label="Vacation Resoultion" type="resolution" file={vacation.files?.resolution} onUpload={upload} />
        </Stack>
      )}
      <SecureComponent permissions={['delete.vacations']}>
        <Dialog open={openConfirmDelete} onClose={() => setOpenConfirmDelete(false)}>
          <DialogTitle>Delete Vacation Request</DialogTitle>
          <DialogContent dividers>
            Are you sure you want to delete vacation request?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDelete(false)}>Cancel</Button>
            <Button onClick={handleOnDeleteClick}>Confirm</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
      <SecureComponent permissions={['create.vacations']}>
        <Dialog open={showVacationRequestForm} onClose={() => setShowVacationRequestForm(false)} fullWidth>
          <DialogTitle>Update Vacation Request</DialogTitle>
          <DialogContent dividers>
            <VacationRequestForm vacation={vacation} onChanged={onEditVacationFormChanged} />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={hideVacartionRequestForm}>CANCEL</Button>
            <Button color="primary" onClick={handleSaveVacationOnClick} disabled={disableSaveButton}>SAVE</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
    </Container>
  );
};